import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { isEmpty } from 'underscore';
import { QUERY_KEYS } from '../../../../queries/queryKeys';
import useGetShoppingCartQuery from '../../../../queries/useGetShoppingCartQuery';
import useAuthStore from '../../../../store/authStore';
import useDatasetStore from '../../../../store/datasetStore';
import { CartTableItem } from '../../../../types/home';
import { IDatasetBrief } from '../../../../types/metadata';
import { ShoppingItem, SummaryPriceItem } from '../../../../types/shopping';
import { formatDate } from '../../../../utils/common';

export default function useShoppingCartData() {
  const datasetBriefs = useDatasetStore((state) => state.datasetBriefs);
  const user = useAuthStore((state) => state.user);

  const [cartData, setCartData] = useState<CartTableItem[]>([]);
  const [shoppingPrice, setShoppingPrice] = useState<SummaryPriceItem>(
    {} as SummaryPriceItem
  );

  const queryClient = useQueryClient();

  const { data: shoppingCartData, isPending } = useGetShoppingCartQuery(
    false,
    true
  );
  const { refetch: refetchWithPrice } = useGetShoppingCartQuery(false, true, {
    enabled: false,
  });
  const { refetch: refetchWithStatusAndPrice } = useGetShoppingCartQuery(
    true,
    true,
    { enabled: false }
  );

  const updateTableData = useCallback(
    (
      items: { [key: string]: { [key: string]: ShoppingItem } },
      showPending: boolean
    ) => {
      const result: CartTableItem[] = [];
      Object.keys(items).forEach((pointName: string) => {
        const itemsPerPoint = items[pointName];
        Object.keys(itemsPerPoint).forEach((category: string) => {
          const item = itemsPerPoint[category];
          const hasError = item.extractionStatus === 'Error';
          const isPending = item.extractionStatus === 'Pending';
          const brief = datasetBriefs.find(
            (brief: IDatasetBrief) => brief.id === item.dataset_id
          );
          result.push({
            id: item.id,
            key: item.order_id,
            datasetId: item.dataset_id,
            Dataset: item.dataset_id,
            Location: item.location,
            TimeStr: `${formatDate(moment(item.start_date))} - ${formatDate(
              moment(item.end_date)
            )}`,
            Price: item.price,
            Detail: [],
            showDetail: false,
            Operation: null,
            isEdit: false,
            pointName: item.point_name,
            orderId: item.order_id,
            category: item.dataset_category,
            acronym: item.dataset_acronym,
            hasError: !showPending ? hasError : hasError || isPending,
            isPending: isPending,
            isFwe: brief?.isFWE,
          });
        });
      });

      setCartData(result);
    },
    [datasetBriefs]
  );

  const applyShoppingCartData = useCallback(
    (
      data: {
        totalPrice?: number;
        discount?: number;
        subtotal?: number;
        shoppingItems: { [key: string]: { [key: string]: ShoppingItem } };
      },
      showPendingItems: boolean
    ) => {
      setShoppingPrice({
        totalPrice: data.totalPrice ?? 0,
        discount: data.discount ?? 0,
        subtotal: data.subtotal ?? 0,
      });
      updateTableData(data.shoppingItems, showPendingItems);
    },
    [updateTableData]
  );

  const prepareData = useCallback(
    async (showPendingItems: boolean) => {
      if (!shoppingCartData) {
        return;
      }

      applyShoppingCartData(shoppingCartData, showPendingItems);
    },
    [shoppingCartData, applyShoppingCartData]
  );

  const refetchShoppingCart = useCallback(
    ({
      checkStatus,
      withPrice,
    }: {
      checkStatus: boolean;
      withPrice: boolean;
    }) => {
      if (checkStatus && withPrice) {
        void refetchWithStatusAndPrice().then((result) => {
          if (result.data) {
            applyShoppingCartData(result.data, true);
          }
        });
      } else if (withPrice) {
        void refetchWithPrice().then((result) => {
          if (result.data) {
            applyShoppingCartData(result.data, false);
          }
        });
      } else {
        void queryClient.refetchQueries({
          queryKey: [QUERY_KEYS.cart.getShoppingCart],
          exact: false,
        });
      }
    },
    [
      queryClient,
      refetchWithPrice,
      refetchWithStatusAndPrice,
      applyShoppingCartData,
    ]
  );

  useEffect(() => {
    const isLoggedIn = !isEmpty(user);
    if (!isLoggedIn) {
      setShoppingPrice({} as SummaryPriceItem);
    } else {
      prepareData(false);
    }
  }, [user, prepareData]);

  return {
    cartData,
    setCartData,
    shoppingPrice,
    prepareData,
    refetchShoppingCart,
    isLoading: isPending,
    shoppingCartData: shoppingCartData,
  };
}
